import React, { useState, useEffect, useRef } from 'react';
import CustomDropdown, { DropdownOption } from 'component/CustomDropdown';
import MultiSelectDropdown from 'component/MultiSelectDropdown';
import {
  ActionRuleModel,
  RuleStatement,
  ConditionItem,
  ActionItem,
  ActionType,
  OperatorType,
  parseActionRules,
  generateActionRules,
  generateId,
} from 'utils/actionRulesParser';
import { FieldOptionInfo, SectionInfo } from './ActionRulesModal';

interface ActionRulesInlineBuilderProps {
  value: string;
  onChange: (newValue: string) => void;
  allFields?: FieldOptionInfo[];
  allSections?: SectionInfo[];
  placeholder?: string;
  forceRawMode?: boolean;
  disablePreCondition?: boolean;
}

const OPERATOR_OPTIONS: DropdownOption[] = [
  { id: '==', name: '== (Equal)' },
  { id: '!=', name: '!= (Not Equal)' },
  { id: '=()=', name: '=()= (Contains)' },
  { id: '!=()=', name: '!=()= (Not Contains)' },
  { id: '>()=', name: '>= (>=)' },
  { id: '>()>', name: '> (>)' },
  { id: '<()=', name: '<= (<=)' },
  { id: '<()<', name: '< (<)' },
];

const ACTION_TYPE_OPTIONS_AFTER_ONLY: DropdownOption[] = [
  { id: 'open_page', name: 'Navigate to Page' },
  { id: 'submit', name: 'Submit Form' },
  { id: 'end', name: 'End Form with Alert' },
  { id: 'field_option_nothing', name: 'Exclusive Option' },
];

const ACTION_TYPE_OPTIONS_PRE_ONLY: DropdownOption[] = [
  { id: 'field_min_value_by_reference', name: 'Min Value Reference' },
  { id: 'matrix_target', name: 'Matrix Target' },
  { id: 'option_reference_by_info', name: 'Option Reference By Info' },
];

const ACTION_TYPE_OPTIONS_SHARED: DropdownOption[] = [
  { id: 'cond_show', name: 'Show Field' },
  { id: 'hide', name: 'Hide Field' },
];

const CUSTOM_ACTION_OPTION: DropdownOption = { id: 'custom', name: 'Custom' };

const CUSTOM_VALUE_OPTION_ID = '__custom_value__';

const getActionTypeOptions = (tab: 'after' | 'pre'): DropdownOption[] => {
  const scoped = tab === 'after' ? ACTION_TYPE_OPTIONS_AFTER_ONLY : ACTION_TYPE_OPTIONS_PRE_ONLY;
  return [...scoped, ...ACTION_TYPE_OPTIONS_SHARED, CUSTOM_ACTION_OPTION];
};

const LOGIC_OPERATOR_OPTIONS: DropdownOption[] = [
  { id: '&&', name: 'AND' },
  { id: '||', name: 'OR' },
];

export const findBestMatchingOption = (val: string, options: string[]): string | undefined => {
  if (!val || !options || options.length === 0) return undefined;
  const cleanVal = val.trim().toLowerCase();

  const exact = options.find((opt) => opt === val);
  if (exact) return exact;

  const caseMatch = options.find((opt) => opt.trim().toLowerCase() === cleanVal);
  if (caseMatch) return caseMatch;

  const sortedWordsVal = cleanVal.split(/\s+/).filter(Boolean).sort().join(' ');
  const wordMatch = options.find((opt) => {
    const sortedOpt = opt.trim().toLowerCase().split(/\s+/).filter(Boolean).sort().join(' ');
    return sortedOpt === sortedWordsVal;
  });
  if (wordMatch) return wordMatch;

  return undefined;
};

export const ActionRulesInlineBuilder: React.FC<ActionRulesInlineBuilderProps> = ({
  value,
  onChange,
  allFields = [],
  allSections = [],
  forceRawMode,
  disablePreCondition = false,
}) => {
  const [isRawMode, setIsRawMode] = useState<boolean>(!!forceRawMode);
  const [activeSubTab, setActiveSubTab] = useState<'after' | 'pre'>('after');
  const [model, setModel] = useState<ActionRuleModel>({ preRules: [], afterRules: [] });
  const lastEmittedValueRef = useRef<string | null>(null);

  useEffect(() => {
    if (forceRawMode !== undefined) {
      setIsRawMode(forceRawMode);
    }
  }, [forceRawMode]);

  useEffect(() => {
    if (value === lastEmittedValueRef.current) {
      return;
    }
    const parsed = parseActionRules(value || '');
    setModel(parsed);
    lastEmittedValueRef.current = value ?? '';
  }, [value]);

  useEffect(() => {
    if (disablePreCondition && activeSubTab === 'pre') {
      setActiveSubTab('after');
    }
  }, [disablePreCondition, activeSubTab]);

  const updateModelAndEmit = (newModel: ActionRuleModel) => {
    setModel(newModel);
    const generatedStr = generateActionRules(newModel);
    lastEmittedValueRef.current = generatedStr;
    onChange(generatedStr);
  };

  const handleRawChange = (rawStr: string) => {
    lastEmittedValueRef.current = rawStr;
    onChange(rawStr);
    const parsed = parseActionRules(rawStr);
    setModel(parsed);
  };

  const currentRules = activeSubTab === 'after' ? model.afterRules : model.preRules;

  const updateCurrentRules = (newRules: RuleStatement[]) => {
    const updatedModel = {
      ...model,
      [activeSubTab === 'after' ? 'afterRules' : 'preRules']: newRules,
    };
    updateModelAndEmit(updatedModel);
  };

  const handleAddRule = () => {
    const defaultField = allFields[0];

    const newRule: RuleStatement = {
      id: generateId(),
      isConditional: true,
      conditions: [
        {
          id: generateId(),
          fieldId: defaultField?.id || '',
          operator: defaultField?.options?.length ? '=()=' : '==',
          valueType: defaultField?.options?.length ? 'selection' : 'string',
          value: defaultField?.options?.[0] || '',
        },
      ],
      action: {
        type: activeSubTab === 'after' ? 'open_page' : 'field_min_value_by_reference',
      },
    };

    updateCurrentRules([...currentRules, newRule]);
  };

  const handleRemoveRule = (ruleId: string) => {
    const newRules = currentRules.filter((r) => r.id !== ruleId);
    updateCurrentRules(newRules);
  };

  const handleUpdateRule = (ruleId: string, updated: Partial<RuleStatement>) => {
    const newRules = currentRules.map((r) => {
      if (r.id === ruleId) {
        return { ...r, ...updated };
      }
      return r;
    });
    updateCurrentRules(newRules);
  };

  const handleAddCondition = (ruleId: string) => {
    const rule = currentRules.find((r) => r.id === ruleId);
    if (!rule) return;

    const defaultField = allFields[0];
    const newCond: ConditionItem = {
      id: generateId(),
      fieldId: defaultField?.id || '',
      operator: defaultField?.options?.length ? '=()=' : '==',
      valueType: defaultField?.options?.length ? 'selection' : 'string',
      value: defaultField?.options?.[0] || '',
    };

    const updatedConditions = [...rule.conditions];
    if (updatedConditions.length > 0) {
      updatedConditions[updatedConditions.length - 1].logicOperator = '||';
    }
    updatedConditions.push(newCond);

    handleUpdateRule(ruleId, { conditions: updatedConditions });
  };

  const handleRemoveCondition = (ruleId: string, condId: string) => {
    const rule = currentRules.find((r) => r.id === ruleId);
    if (!rule) return;

    const updatedConditions = rule.conditions.filter((c) => c.id !== condId);
    if (updatedConditions.length > 0) {
      delete updatedConditions[updatedConditions.length - 1].logicOperator;
    }

    handleUpdateRule(ruleId, { conditions: updatedConditions });
  };

  const handleUpdateCondition = (
    ruleId: string,
    condId: string,
    updated: Partial<ConditionItem>
  ) => {
    const rule = currentRules.find((r) => r.id === ruleId);
    if (!rule) return;

    const updatedConditions = rule.conditions.map((c) => {
      if (c.id === condId) {
        let newOperator = updated.operator !== undefined ? updated.operator : c.operator;
        let newValueType = c.valueType;

        if (updated.operator) {
          if (updated.operator === '=()=' || updated.operator === '!=()=') {
            newValueType = 'selection';
          } else if (updated.operator === '==' || updated.operator === '!=') {
            newValueType = 'string';
          }
        }

        const targetFieldId = updated.fieldId !== undefined ? updated.fieldId : c.fieldId;
        const targetField = allFields.find((f) => f.id === targetFieldId);

        let finalValue = updated.value !== undefined ? updated.value : c.value;
        if (targetField?.options && targetField.options.length > 0 && finalValue) {
          const matched = findBestMatchingOption(finalValue, targetField.options);
          if (matched) {
            finalValue = matched;
          }
        }

        if (updated.fieldId && updated.fieldId !== c.fieldId) {
          const selectedField = allFields.find((f) => f.id === updated.fieldId);
          if (selectedField?.options && selectedField.options.length > 0) {
            const matched = findBestMatchingOption(c.value, selectedField.options) || selectedField.options[0];
            return {
              ...c,
              ...updated,
              operator: newOperator,
              valueType: newValueType,
              value: matched,
            };
          }
        }
        return { ...c, ...updated, value: finalValue, valueType: newValueType };
      }
      return c;
    });

    handleUpdateRule(ruleId, { conditions: updatedConditions });
  };

  const handleUpdateAction = (ruleId: string, updatedAction: Partial<ActionItem>) => {
    const rule = currentRules.find((r) => r.id === ruleId);
    if (!rule) return;

    handleUpdateRule(ruleId, {
      action: { ...rule.action, ...updatedAction },
    });
  };

  const handleToggleConditional = (ruleId: string, checked: boolean) => {
    const rule = currentRules.find((r) => r.id === ruleId);
    if (!rule) return;

    if (checked && rule.conditions.length === 0) {
      const defaultField = allFields[0];
      const newCond: ConditionItem = {
        id: generateId(),
        fieldId: defaultField?.id || '',
        operator: defaultField?.options?.length ? '=()=' : '==',
        valueType: defaultField?.options?.length ? 'selection' : 'string',
        value: defaultField?.options?.[0] || '',
      };
      handleUpdateRule(ruleId, { isConditional: true, conditions: [newCond] });
      return;
    }

    handleUpdateRule(ruleId, { isConditional: checked });
  };

  const fieldMultiOptions = allFields.map((f) => ({
    code: f.id || '',
    name: f.label ? `${f.label}` : f.id || '',
    subTitle: f.sectionName ? `Page: ${f.sectionName}` : undefined,
  }));

  const sectionMultiOptions = allSections.map((s) => ({
    code: s.id || s.name || '',
    name: s.name,
  }));

  const dropdownBtnClass =
    "btn d-flex align-items-center justify-content-between px-2 py-0 text-dark border rounded text-start w-100";
  const dropdownBtnStyle: React.CSSProperties = {
    fontSize: '0.75rem',
    minHeight: '28px',
    background: '#fff',
    borderColor: '#dfe3e8',
  };
  const preCount = model.preRules?.length || 0;
  const afterCount = model.afterRules?.length || 0;

  return (
    <div
      className="rounded-3 bg-white"
      style={{
        border: '1px solid #e4e7ec',
        boxShadow: '0 1px 3px rgba(16,24,40,0.06)',
        position: 'relative',
        zIndex: 1,
        overflow: 'visible',
      }}
    >
      {/* ===== Header Bar ===== */}
      <div
        className="d-flex justify-content-between align-items-center flex-wrap gap-1"
        style={{
          padding: '8px 10px',
          background: 'linear-gradient(to bottom, #fafbfc, #f4f6f8)',
          borderBottom: '1px solid #e4e7ec',
          borderTopLeftRadius: '11px',
          borderTopRightRadius: '11px',
        }}
      >
        <div
          className="d-flex align-items-center gap-1 p-1"
          style={{ background: '#eef1f4', borderRadius: '8px' }}
        >
          <button
            type="button"
            disabled={isRawMode || disablePreCondition}
            className="btn py-0 px-2 d-flex align-items-center"
            style={{
              fontSize: '0.74rem',
              fontWeight: 500,
              borderRadius: '6px',
              cursor: (isRawMode || disablePreCondition) ? 'not-allowed' : 'pointer',
              background: activeSubTab === 'pre' && !isRawMode ? '#ffffff' : 'transparent',
              color: activeSubTab === 'pre' && !isRawMode ? '#1d4ed8' : '#6b7280',
              boxShadow:
                activeSubTab === 'pre' && !isRawMode
                  ? '0 1px 2px rgba(16,24,40,0.08)'
                  : 'none',
              border: 'none',
              opacity: isRawMode || disablePreCondition ? 0.5 : 1,
            }}
            title={disablePreCondition ? 'Pre Condition tidak tersedia untuk field-level rules' : undefined}
            onClick={() => {
              if (isRawMode || disablePreCondition) return;
              setActiveSubTab('pre');
              setIsRawMode(false);
            }}
          >
            <i className="fa-solid fa-sliders me-1"></i>
            Pre Condition
            <span
              className="badge ms-2"
              style={{
                fontSize: '0.62rem',
                borderRadius: '10px',
                background: activeSubTab === 'pre' && !isRawMode ? '#dbeafe' : '#e5e7eb',
                color: activeSubTab === 'pre' && !isRawMode ? '#1d4ed8' : '#6b7280',
              }}
            >
              {preCount}
            </span>
          </button>
          <button
            type="button"
            disabled={isRawMode}
            className="btn py-0 px-2 d-flex align-items-center"
            style={{
              fontSize: '0.74rem',
              fontWeight: 500,
              borderRadius: '6px',
              cursor: isRawMode ? 'not-allowed' : 'pointer',
              background: activeSubTab === 'after' && !isRawMode ? '#ffffff' : 'transparent',
              color: activeSubTab === 'after' && !isRawMode ? '#1d4ed8' : '#6b7280',
              boxShadow:
                activeSubTab === 'after' && !isRawMode
                  ? '0 1px 2px rgba(16,24,40,0.08)'
                  : 'none',
              border: 'none',
              opacity: isRawMode ? 0.5 : 1,
            }}
            onClick={() => {
              if (isRawMode) return;
              setActiveSubTab('after');
              setIsRawMode(false);
            }}
          >
            <i className="fa-solid fa-bolt me-1"></i>
            Post Action
            <span
              className="badge ms-2"
              style={{
                fontSize: '0.62rem',
                borderRadius: '10px',
                background: activeSubTab === 'after' && !isRawMode ? '#dbeafe' : '#e5e7eb',
                color: activeSubTab === 'after' && !isRawMode ? '#1d4ed8' : '#6b7280',
              }}
            >
              {afterCount}
            </span>
          </button>
        </div>

        <button
          type="button"
          className="btn btn-sm py-0 px-2 d-flex align-items-center"
          style={{
            fontSize: '0.72rem',
            color: '#1d4ed8',
            borderRadius: '6px',
            background: 'transparent',
            border: '1px solid #dbeafe',
          }}
          onClick={() => setIsRawMode(!isRawMode)}
        >
          <i className={`fa-solid ${isRawMode ? 'fa-border-all' : 'fa-code'} me-1`}></i>
          {isRawMode ? 'Code Mode' : 'Visual Mode (Beta)'}
        </button>
      </div>

      {/* ===== Main Content ===== */}
      <div style={{ padding: '10px' }}>
        {isRawMode ? (
          <div>
            <textarea
              className="form-control form-control-sm font-monospace"
              rows={3}
              value={value || ''}
              onChange={(e) => handleRawChange(e.target.value)}
              placeholder=""
              style={{
                borderRadius: '8px',
                border: '1px solid #dfe3e8',
                background: '#fafbfc',
                fontSize: '0.78rem',
              }}
            />
          </div>
        ) : (
          <div>
            {currentRules.length === 0 ? (
              <div
                className="text-center"
                style={{
                  padding: '20px 12px',
                  borderRadius: '10px',
                  background: '#fafbfc',
                  border: '1px dashed #dfe3e8',
                }}
              >
                <i
                  className="fa-solid fa-wand-magic-sparkles d-block mb-2"
                  style={{ fontSize: '1.1rem', color: '#9aa4b2' }}
                ></i>
                <span className="text-muted d-block mb-2" style={{ fontSize: '0.75rem' }}>
                  No rules added yet.
                </span>
                <button
                  type="button"
                  className="btn py-0 px-3"
                  style={{
                    fontSize: '0.75rem',
                    borderRadius: '6px',
                    background: '#1d4ed8',
                    color: '#fff',
                    border: 'none',
                  }}
                  onClick={handleAddRule}
                >
                  <i className="fa-solid fa-plus me-1"></i> Add Rule
                </button>
              </div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {currentRules.map((rule, rIdx) => (
                  <div
                    key={rule.id}
                    style={{
                      borderRadius: '10px',
                      border: '1px solid #e4e7ec',
                      background: '#ffffff',
                      position: 'relative',
                      zIndex: currentRules.length - rIdx,
                      overflow: 'visible',
                    }}
                  >
                    {/* Rule Header */}
                    <div
                      className="d-flex justify-content-between align-items-center"
                      style={{
                        padding: '6px 10px',
                        background: '#f8fafc',
                        borderBottom: '1px solid #eef1f4',
                        borderTopLeftRadius: '9px',
                        borderTopRightRadius: '9px',
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span
                          className="d-inline-flex align-items-center justify-content-center"
                          style={{
                            width: '22px',
                            height: '22px',
                            borderRadius: '6px',
                            background: '#1d4ed8',
                            color: '#fff',
                            fontSize: '0.68rem',
                            fontWeight: 600,
                          }}
                        >
                          {rIdx + 1}
                        </span>
                        <div className="form-check form-switch mb-0 d-flex align-items-center gap-2">
                          <input
                            className="form-check-input m-0"
                            type="checkbox"
                            style={{ cursor: 'pointer' }}
                            id={`inline-cond-${rule.id}`}
                            checked={rule.isConditional}
                            onChange={(e) => handleToggleConditional(rule.id, e.target.checked)}
                          />
                          <label
                            className="form-check-label text-muted m-0"
                            htmlFor={`inline-cond-${rule.id}`}
                            style={{ fontSize: '0.72rem', cursor: 'pointer' }}
                          >
                            Use Condition (IF)
                          </label>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="btn p-1 d-flex align-items-center justify-content-center"
                        style={{
                          borderRadius: '6px',
                          color: '#dc2626',
                          background: 'transparent',
                          border: 'none',
                          width: '26px',
                          height: '26px',
                        }}
                        onClick={() => handleRemoveRule(rule.id)}
                        title="Delete Rule"
                      >
                        <i className="fa-solid fa-trash-can" style={{ fontSize: '0.78rem' }}></i>
                      </button>
                    </div>

                    <div style={{ padding: '8px 10px' }}>
                      {/* Conditions Block */}
                      {rule.isConditional && (
                        <div
                          style={{
                            padding: '8px',
                            borderRadius: '8px',
                            background: '#f8fafc',
                            border: '1px solid #eef1f4',
                            marginBottom: '8px',
                          }}
                        >
                          {rule.conditions.map((cond, cIdx) => {
                            const selectedField = allFields.find((f) => f.id === cond.fieldId);
                            const hasOptions =
                              selectedField?.options && selectedField.options.length > 0;
                            const optionValueItems: DropdownOption[] = (selectedField?.options || []).map(
                              (opt) => ({
                                id: opt,
                                name: opt,
                              })
                            );

                            const matchedOpt = selectedField?.options
                              ? findBestMatchingOption(cond.value, selectedField.options)
                              : undefined;

                            const isCustomValue =
                              hasOptions && (!cond.value || !matchedOpt);

                            const activeDropdownValue = isCustomValue
                              ? CUSTOM_VALUE_OPTION_ID
                              : matchedOpt || null;

                            return (
                              <div key={cond.id} className="mb-2">
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                  <div
                                    className="d-flex align-items-center gap-1"
                                    style={{ flexShrink: 0 }}
                                  >
                                    <span
                                      className="d-inline-flex align-items-center justify-content-center"
                                      style={{
                                        fontSize: '0.64rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.4px',
                                        minWidth: '30px',
                                        height: '24px',
                                        borderRadius: '6px',
                                        background: '#1d4ed8',
                                        color: '#fff',
                                      }}
                                    >
                                      IF
                                    </span>
                                    <button
                                      type="button"
                                      className="btn py-0 px-2 d-flex align-items-center justify-content-center gap-1"
                                      style={{
                                        fontSize: '0.68rem',
                                        fontWeight: 600,
                                        height: '28px',
                                        borderRadius: '6px',
                                        whiteSpace: 'nowrap',
                                        background:
                                          cond.targetType === 'additional_info' ? '#0891b2' : '#ffffff',
                                        color:
                                          cond.targetType === 'additional_info' ? '#fff' : '#374151',
                                        border:
                                          cond.targetType === 'additional_info'
                                            ? '1px solid #0891b2'
                                            : '1px solid #dfe3e8',
                                      }}
                                      title="Klik untuk beralih antara Form Field dan Additional Info"
                                      onClick={() => {
                                        const newType =
                                          cond.targetType === 'additional_info'
                                            ? 'field'
                                            : 'additional_info';
                                        handleUpdateCondition(rule.id, cond.id, {
                                          targetType: newType,
                                        });
                                      }}
                                    >
                                      <i
                                        className={`fa-solid ${cond.targetType === 'additional_info'
                                          ? 'fa-database'
                                          : 'fa-list-check'
                                          }`}
                                        style={{ fontSize: '0.65rem' }}
                                      ></i>
                                      {cond.targetType === 'additional_info'
                                        ? 'Additional Info'
                                        : 'Form Field'}
                                    </button>
                                  </div>

                                  <div style={{ flex: '1 1 200px', minWidth: '160px' }}>
                                    {cond.targetType === 'additional_info' ? (
                                      <input
                                        type="text"
                                        className="form-control form-control-sm py-0 px-2"
                                        style={{
                                          fontSize: '0.75rem',
                                          height: '28px',
                                          borderColor: '#dfe3e8',
                                        }}
                                        placeholder="Key Info (misal: Material)"
                                        value={cond.fieldId}
                                        onChange={(e) =>
                                          handleUpdateCondition(rule.id, cond.id, {
                                            fieldId: e.target.value,
                                          })
                                        }
                                      />
                                    ) : fieldMultiOptions.length > 0 ? (
                                      <MultiSelectDropdown
                                        single={true}
                                        options={fieldMultiOptions}
                                        value={cond.fieldId ? [cond.fieldId] : []}
                                        onChange={(selected) =>
                                          handleUpdateCondition(rule.id, cond.id, {
                                            fieldId: selected[0] || '',
                                          })
                                        }
                                        placeholder="-- Select Field --"
                                        previewKey="name"
                                        subKey="subTitle"
                                        btnClassName={dropdownBtnClass}
                                        buttonStyle={dropdownBtnStyle}
                                      />
                                    ) : (
                                      <input
                                        type="text"
                                        className="form-control form-control-sm py-0 px-2"
                                        style={{ fontSize: '0.75rem', height: '28px' }}
                                        placeholder="Field ID"
                                        value={cond.fieldId}
                                        onChange={(e) =>
                                          handleUpdateCondition(rule.id, cond.id, {
                                            fieldId: e.target.value,
                                          })
                                        }
                                      />
                                    )}
                                  </div>

                                  <div style={{ flex: '0 1 150px', minWidth: '130px' }}>
                                    <CustomDropdown
                                      value={cond.operator}
                                      options={OPERATOR_OPTIONS}
                                      onChange={(opt) =>
                                        handleUpdateCondition(rule.id, cond.id, {
                                          operator: opt.id as OperatorType,
                                        })
                                      }
                                      placeholder="Operator"
                                      buttonClassName={dropdownBtnClass}
                                      buttonStyle={dropdownBtnStyle}
                                      containerClassName="w-100"
                                    />
                                  </div>

                                  {/* Value / Option */}
                                  <div
                                    className="d-flex flex-wrap align-items-center gap-1"
                                    style={{ flex: '1 1 260px', minWidth: '200px' }}
                                  >
                                    {hasOptions ? (
                                      <>
                                        <div
                                          style={{
                                            flex: isCustomValue ? '1 1 160px' : '1 1 100%',
                                            minWidth: '140px',
                                          }}
                                        >
                                          <CustomDropdown
                                            value={activeDropdownValue}
                                            options={[
                                              ...optionValueItems,
                                              {
                                                id: CUSTOM_VALUE_OPTION_ID,
                                                name: 'Custom Value',
                                              },
                                            ]}
                                            onChange={(opt) => {
                                              if (opt.id === CUSTOM_VALUE_OPTION_ID) {
                                                handleUpdateCondition(rule.id, cond.id, {
                                                  value: '',
                                                  valueType: 'selection',
                                                });
                                              } else {
                                                handleUpdateCondition(rule.id, cond.id, {
                                                  value: String(opt.id || ''),
                                                  valueType: 'selection',
                                                });
                                              }
                                            }}
                                            placeholder="-- Option --"
                                            buttonClassName={dropdownBtnClass}
                                            buttonStyle={dropdownBtnStyle}
                                            containerClassName="w-100"
                                          />
                                        </div>
                                        {isCustomValue && (
                                          <div
                                            style={{ flex: '1 1 160px', minWidth: '140px' }}
                                          >
                                            <input
                                              type="text"
                                              className="form-control form-control-sm py-0 px-2"
                                              style={{
                                                fontSize: '0.75rem',
                                                height: '28px',
                                                borderColor: '#dfe3e8',
                                              }}
                                              placeholder="Custom Value"
                                              value={cond.value}
                                              autoFocus
                                              onChange={(e) =>
                                                handleUpdateCondition(rule.id, cond.id, {
                                                  value: e.target.value,
                                                  valueType: 'selection',
                                                })
                                              }
                                            />
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <input
                                        type="text"
                                        className="form-control form-control-sm py-0 px-2 w-100"
                                        style={{ fontSize: '0.75rem', height: '28px' }}
                                        placeholder="Value (e.g. Yes)"
                                        value={cond.value}
                                        onChange={(e) =>
                                          handleUpdateCondition(rule.id, cond.id, {
                                            value: e.target.value,
                                          })
                                        }
                                      />
                                    )}
                                  </div>

                                  {rule.conditions.length > 1 && (
                                    <button
                                      type="button"
                                      className="btn p-0 d-flex align-items-center justify-content-center"
                                      style={{
                                        flexShrink: 0,
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '6px',
                                        background: '#fee2e2',
                                        color: '#dc2626',
                                        border: 'none',
                                      }}
                                      onClick={() => handleRemoveCondition(rule.id, cond.id)}
                                      title="Remove Condition"
                                    >
                                      <i className="fa-solid fa-xmark" style={{ fontSize: '0.7rem' }}></i>
                                    </button>
                                  )}
                                </div>

                                {cIdx < rule.conditions.length - 1 && (
                                  <div
                                    className="d-flex align-items-center my-2"
                                    style={{ paddingLeft: '42px' }}
                                  >
                                    <CustomDropdown
                                      value={cond.logicOperator || '&&'}
                                      options={LOGIC_OPERATOR_OPTIONS}
                                      onChange={(opt) =>
                                        handleUpdateCondition(rule.id, cond.id, {
                                          logicOperator: opt.id as '&&' | '||',
                                        })
                                      }
                                      buttonClassName="btn py-0 px-2 d-flex align-items-center justify-content-center"
                                      buttonStyle={{
                                        fontSize: '0.68rem',
                                        fontWeight: 700,
                                        height: '24px',
                                        minWidth: '46px',
                                        borderRadius: '6px',
                                        background: '#eef2ff',
                                        color: '#4338ca',
                                        border: '1px solid #c7d2fe',
                                      }}
                                      containerClassName=""
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          <button
                            type="button"
                            className="btn btn-link p-0 text-decoration-none d-flex align-items-center"
                            style={{
                              fontSize: '0.72rem',
                              color: '#1d4ed8',
                              marginTop: '4px',
                            }}
                            onClick={() => handleAddCondition(rule.id)}
                          >
                            <i className="fa-solid fa-plus me-1" style={{ fontSize: '0.65rem' }}></i>
                            Add Condition (AND/OR)
                          </button>
                        </div>
                      )}

                      {/* Action Row */}
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <span
                          className="d-inline-flex align-items-center justify-content-center"
                          style={{
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            letterSpacing: '0.4px',
                            minWidth: '48px',
                            height: '24px',
                            borderRadius: '6px',
                            background: '#059669',
                            color: '#fff',
                            flexShrink: 0,
                          }}
                        >
                          THEN
                        </span>

                        <div style={{ flex: '0 1 200px', minWidth: '170px' }}>
                          <CustomDropdown
                            value={rule.action.type}
                            options={getActionTypeOptions(activeSubTab)}
                            onChange={(opt) =>
                              handleUpdateAction(rule.id, {
                                type: opt.id as ActionType,
                              })
                            }
                            placeholder="Action Type"
                            buttonClassName={dropdownBtnClass}
                            buttonStyle={dropdownBtnStyle}
                            containerClassName="w-100"
                          />
                        </div>

                        <div style={{ flex: '1 1 240px', minWidth: '200px' }}>
                          {rule.action.type === 'open_page' && (
                            <div>
                              {sectionMultiOptions.length > 0 ? (
                                <MultiSelectDropdown
                                  single={true}
                                  options={sectionMultiOptions}
                                  value={rule.action.pageId ? [rule.action.pageId] : []}
                                  onChange={(selected) =>
                                    handleUpdateAction(rule.id, {
                                      pageId: selected[0] || '',
                                    })
                                  }
                                  placeholder="-- Target Page/Section --"
                                  btnClassName={dropdownBtnClass}
                                  buttonStyle={dropdownBtnStyle}
                                />
                              ) : (
                                <input
                                  type="text"
                                  className="form-control form-control-sm py-0 px-2"
                                  style={{ fontSize: '0.75rem', height: '28px' }}
                                  placeholder="Page ID"
                                  value={rule.action.pageId || ''}
                                  onChange={(e) =>
                                    handleUpdateAction(rule.id, { pageId: e.target.value })
                                  }
                                />
                              )}
                            </div>
                          )}

                          {(rule.action.type === 'cond_show' ||
                            rule.action.type === 'hide' ||
                            rule.action.type === 'matrix_target') && (
                              <div>
                                {fieldMultiOptions.length > 0 ? (
                                  <MultiSelectDropdown
                                    single={true}
                                    options={fieldMultiOptions}
                                    value={
                                      rule.action.targetFieldId ? [rule.action.targetFieldId] : []
                                    }
                                    onChange={(selected) =>
                                      handleUpdateAction(rule.id, {
                                        targetFieldId: selected[0] || '',
                                      })
                                    }
                                    placeholder="-- Select Target Field --"
                                    previewKey="name"
                                    subKey="subTitle"
                                    btnClassName={dropdownBtnClass}
                                    buttonStyle={dropdownBtnStyle}
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    className="form-control form-control-sm py-0 px-2"
                                    style={{ fontSize: '0.75rem', height: '28px' }}
                                    placeholder="Target Field ID"
                                    value={rule.action.targetFieldId || ''}
                                    onChange={(e) =>
                                      handleUpdateAction(rule.id, {
                                        targetFieldId: e.target.value,
                                      })
                                    }
                                  />
                                )}
                              </div>
                            )}

                          {rule.action.type === 'end' && (
                            <input
                              type="text"
                              className="form-control form-control-sm py-0 px-2"
                              style={{ fontSize: '0.75rem', height: '28px' }}
                              placeholder="Alert Message"
                              value={rule.action.infoMessage || ''}
                              onChange={(e) =>
                                handleUpdateAction(rule.id, {
                                  infoMessage: e.target.value,
                                })
                              }
                            />
                          )}

                          {rule.action.type === 'field_option_nothing' && (
                            <div className="d-flex flex-wrap gap-1">
                              <div style={{ flex: '1 1 140px', minWidth: '120px' }}>
                                <MultiSelectDropdown
                                  single={true}
                                  options={fieldMultiOptions}
                                  value={
                                    rule.action.targetFieldId ? [rule.action.targetFieldId] : []
                                  }
                                  onChange={(selected) =>
                                    handleUpdateAction(rule.id, {
                                      targetFieldId: selected[0] || '',
                                    })
                                  }
                                  placeholder="-- Field --"
                                  previewKey="name"
                                  subKey="subTitle"
                                  btnClassName={dropdownBtnClass}
                                  buttonStyle={dropdownBtnStyle}
                                />
                              </div>
                              <div style={{ flex: '1 1 140px', minWidth: '120px' }}>
                                <input
                                  type="text"
                                  className="form-control form-control-sm py-0 px-2"
                                  style={{ fontSize: '0.75rem', height: '28px' }}
                                  placeholder="Exclusive Option"
                                  value={rule.action.exclusiveOption || ''}
                                  onChange={(e) =>
                                    handleUpdateAction(rule.id, {
                                      exclusiveOption: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </div>
                          )}

                          {rule.action.type === 'field_min_value_by_reference' && (
                            <div className="d-flex flex-wrap gap-1">
                              <div style={{ flex: '1 1 140px', minWidth: '120px' }}>
                                <MultiSelectDropdown
                                  single={true}
                                  options={fieldMultiOptions}
                                  value={
                                    rule.action.targetFieldId ? [rule.action.targetFieldId] : []
                                  }
                                  onChange={(selected) =>
                                    handleUpdateAction(rule.id, {
                                      targetFieldId: selected[0] || '',
                                    })
                                  }
                                  placeholder="-- Target --"
                                  previewKey="name"
                                  subKey="subTitle"
                                  btnClassName={dropdownBtnClass}
                                  buttonStyle={dropdownBtnStyle}
                                />
                              </div>
                              <div style={{ flex: '1 1 140px', minWidth: '120px' }}>
                                <MultiSelectDropdown
                                  single={true}
                                  options={fieldMultiOptions}
                                  value={rule.action.refFieldId ? [rule.action.refFieldId] : []}
                                  onChange={(selected) =>
                                    handleUpdateAction(rule.id, {
                                      refFieldId: selected[0] || '',
                                    })
                                  }
                                  placeholder="-- Ref Field --"
                                  previewKey="name"
                                  subKey="subTitle"
                                  btnClassName={dropdownBtnClass}
                                  buttonStyle={dropdownBtnStyle}
                                />
                              </div>
                            </div>
                          )}

                          {rule.action.type === 'option_reference_by_info' && (
                            <div className="d-flex flex-wrap gap-1">
                              <div style={{ flex: '1 1 140px', minWidth: '120px' }}>
                                <MultiSelectDropdown
                                  single={true}
                                  options={fieldMultiOptions}
                                  value={
                                    rule.action.targetFieldId ? [rule.action.targetFieldId] : []
                                  }
                                  onChange={(selected) =>
                                    handleUpdateAction(rule.id, {
                                      targetFieldId: selected[0] || '',
                                    })
                                  }
                                  placeholder="-- Field --"
                                  previewKey="name"
                                  subKey="subTitle"
                                  btnClassName={dropdownBtnClass}
                                  buttonStyle={dropdownBtnStyle}
                                />
                              </div>
                              <div style={{ flex: '1 1 140px', minWidth: '120px' }}>
                                <input
                                  type="text"
                                  className="form-control form-control-sm py-0 px-2"
                                  style={{ fontSize: '0.75rem', height: '28px' }}
                                  placeholder="Info Key (e.g. Material)"
                                  value={rule.action.infoKey || ''}
                                  onChange={(e) =>
                                    handleUpdateAction(rule.id, { infoKey: e.target.value })
                                  }
                                />
                              </div>
                            </div>
                          )}

                          {rule.action.type === 'custom' && (
                            <input
                              type="text"
                              className="form-control form-control-sm py-0 px-2 font-monospace"
                              style={{ fontSize: '0.75rem', height: '28px' }}
                              placeholder="Custom syntax"
                              value={rule.action.rawText || ''}
                              onChange={(e) =>
                                handleUpdateAction(rule.id, { rawText: e.target.value })
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn align-self-start d-flex align-items-center"
                  style={{
                    fontSize: '0.75rem',
                    padding: '5px 12px',
                    borderRadius: '8px',
                    background: '#fff',
                    color: '#1d4ed8',
                    border: '1px dashed #93c5fd',
                    position: 'relative',
                    zIndex: 0,
                  }}
                  onClick={handleAddRule}
                >
                  <i className="fa-solid fa-plus me-1"></i> Add Rule
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
